<template>
  <div class="mb-3 card">
    <div class="card-header p-2">
      <table class="w-100">
        <tr>
          <td
            style="width: 35px;"
          >
            <div class="h-100 d-flex align-items-center">
              <div class="badge badge-dark badge-pill">
                {{ researchItem.order + 1 }}
              </div>
            </div>
          </td>
          <td>
            <h5
              class="m-0 title-truncated"
              v-html="formattedItemText(researchItem.title)"
            />
          </td>
          <td
            class="text-right"
            style="width: 45px;"
          >
            <span
              v-if="!['media', 'no_field'].includes(researchItem.item.render_type)"
              class="badge badge-pill badge-default"
            >
              {{ answers.length }}
            </span>
          </td>
        </tr>
      </table>
      <ul
        class="nav nav-sm nav-pills nav-responsive-sm m-2"
        style="min-height: 32px"
      >
        <li
          class="nav-item"
          v-for="option in dataViewOptions"
          :key="option"
        >
          <a
            class="nav-link"
            :class="{active: option === selectedItemView}"
            @click="selectedItemView = option"
            href="javascript:void(0)"
          >
            <i
              class="fa mr-2"
              :class="[`fa-${DATA_VIEW_OPTIONS[option].icon}`]"
            ></i>
            {{ $t(DATA_VIEW_OPTIONS[option].displayName) }}
          </a>
        </li>
      </ul>
    </div>
    <div
      v-if="loading"
      class="item-view-body w-100 d-flex align-items-center justify-content-center"
    >
      <v-loader/>
    </div>
    <div
      class="item-view-body"
      v-else-if="answers.length > 0 || selectedItemView === 'media'"
    >
      <transition name="fade-nl">
        <v-item-data-raw
          :answers="answers"
          :color-by-option="makeColorByOption()"
          :render-type="item.render_type"
          v-if="selectedItemView === 'raw'"
        />
      </transition>

      <transition name="fade-nl">
        <!-- Word count for single/multi select items + histogram for numeric items -->
        <v-item-data-histogram
          v-if="selectedItemView === 'histogramFrequencies'"
          :frequencies="frequencies"
          :title="researchItem.title"
          :colors="colors"
          :swap-colors="true"
        />
      </transition>

      <transition name="fade-nl">
        <!-- Histogram for the distribution of numeric items -->
        <v-item-data-histogram
          v-if="selectedItemView === 'histogramNumeric'"
          :title="researchItem.title"
          :frequencies="frequencies"
          :colors="renderType !== 'numeric' ? colors: [COLOR_SCHEMES.red[2]]"
          :swap-colors="renderType !== 'numeric'"
        />
      </transition>

      <transition name="fade-nl">
        <!-- Stacked bar chart for the distribution of single and multi choice items -->
        <div v-if="selectedItemView === 'stacked100'">
          <v-item-stacked-100
            :title="researchItem.title"
            :frequencies="frequencies"
            :colors="colors"
          />
        </div>
      </transition>

      <transition name="fade-nl">
        <v-item-data-average
          :title="researchItem.title"
          :answers="answers"
          :total-average="totalAverage"
          :industry-average="industryAverage"
          :previous-commercials-average="previousCommercialsAverage"
          :month-average="monthAverage"
          v-if="selectedItemView === 'averages'"
        />
      </transition>

      <transition name="fade-nl">
        <v-item-data-top-10-words
          :answers="answers"
          v-if="selectedItemView === 'top10Words'"
        />
      </transition>

      <transition name="fade-nl">
        <v-item-media
          v-if="selectedItemView === 'media'"
          :research-item="researchItem"
        />
      </transition>

      <div
        v-if="selectedItemView === 'textualItem'"
        class="w-100 h-100 d-flex flex-column justify-content-center align-items-center text-muted"
      >
        <i class="fa fa-info-circle mb-2"></i>
        {{ $t('sentence.info_text') }}
      </div>

      <div
        v-if="selectedItemView === 'compareMode'"
        class="w-100 h-100 d-flex flex-column justify-content-center align-items-center text-muted"
      >
        <v-item-data-compare
          :research="research"
          :research-item="researchItem"
        />
      </div>

      <div
        v-if="selectedItemView === 'noData'"
        class="w-100 h-100 d-flex flex-column justify-content-center align-items-center text-muted"
      >
        <i class="fa fa-exclamation-circle mb-2"></i>
        {{ $t('sentence.no_data') }}
      </div>
    </div>
    <div v-else class="item-view-body">
      <div class="h-100 d-flex flex-column justify-content-center align-items-center text-muted">
        {{ $t('phrase.no_answers_found') }}
      </div>
    </div>
  </div>
</template>

<script>
import ItemDataAverageWidget from '@/organisation/components/research/widgets/ItemDataAverageWidget.vue';
import ItemDataHistogramWidget from '@/organisation/components/research/widgets/ItemDataHistogramWidget.vue';
import ItemDataRawWidget from '@/organisation/components/research/widgets/ItemDataRawWidget.vue';
import ItemDataStacked100Widget from '@/organisation/components/research/widgets/ItemDataStacked100Widget.vue';
import ItemDataTop10WordsWidget from '@/organisation/components/research/widgets/ItemDataTop10WordsWidget.vue';
import ItemMedia from '@/organisation/components/research/ItemMedia.vue';
import Loader from '@/common/components/Loader.vue';
import {
  DATA_VIEW_OPTIONS,
  ITEM_DATA_VIEW_OPTIONS,
} from '@/organisation/constants';
import {
  cleanedItemText,
  formattedItemText,
} from '@/common/services/items';
import { mapGetters } from 'vuex';
import ItemDataCompareWidget from '@/organisation/components/research/widgets/ItemDataCompareWidget.vue';
import { calculateFrequencies } from '@/organisation/services/researches';
import {
  COLOR_SCHEMES,
  COLORS_WITHOUT_WHITE,
} from '@/common/utils/constants';

export default {
  name: 'ItemData',
  components: {
    'v-item-data-average': ItemDataAverageWidget,
    'v-item-stacked-100': ItemDataStacked100Widget,
    'v-item-data-top-10-words': ItemDataTop10WordsWidget,
    'v-item-data-histogram': ItemDataHistogramWidget,
    'v-item-data-raw': ItemDataRawWidget,
    'v-item-data-compare': ItemDataCompareWidget,
    'v-item-media': ItemMedia,
    'v-loader': Loader,
  },
  props: {
    research: {
      type: Object,
      required: true,
    },
    researchItem: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      COLOR_SCHEMES,
      DATA_VIEW_OPTIONS,
      selectedItemView: null,
      frequencies: null,
    };
  },
  methods: {
    cleanedItemText,
    formattedItemText,
    calculateFrequencies() {
      if (this.renderType === 'numeric') {
        // Frequencies for numeric items;
        const frequencies = [];
        const answers = this.answers
          .filter((answer) => answer.value_num !== null)
          .map((answer) => answer.value_num);
        for (let i = 1; i <= 10; i += 1) {
          const frequency = answers.filter((ans) => (ans === i)).length;
          frequencies.push([i, (frequency / answers.length) * 100]);
        }

        // Frequencies are returned because they shouldn't be ordered;
        return frequencies;
      }

      if (['multi_value', 'single_value'].includes(this.renderType)) {
        return calculateFrequencies(
          this.answers,
          this.researchItem,
        );
      }
      return [];
    },
    makeColorByOption() {
      return this.researchItem.item.options.reduce((acc, option) => {
        acc[option.value] = option.color;
        if (option.short_value) {
          acc[option.short_value] = option.color;
        }
        return acc;
      }, {});
    },
  },
  watch: {
    loading(newValue) {
      if (!newValue) {
        this.frequencies = this.calculateFrequencies();
      }
    },
  },
  computed: {
    ...mapGetters('researches', [
      'data',
      'loadingResearchStatistics',
      'loadingResearchReviewSessions',
    ]),
    loading() {
      return this.loadingResearchStatistics || this.loadingResearchReviewSessions;
    },
    colors() {
      const colorByOption = this.makeColorByOption();
      const defaultColors = Object.values(COLORS_WITHOUT_WHITE);
      return this.frequencies.map((freq, index) => (
        colorByOption[freq[0]] || defaultColors[index % defaultColors.length]
      ));
    },
    answers() {
      return this.data.answersByResearchItem[this.researchItem.id] || [];
    },
    config() {
      return this.researchItem.config;
    },
    dataViewOptions() {
      if (this.item.render_type === 'no_field' || this.item.render_type === 'media') {
        return [];
      }
      return ITEM_DATA_VIEW_OPTIONS[this.item.render_type];
    },
    item() {
      return this.researchItem.item;
    },
    renderType() {
      if (this.item.is_numeric) {
        return 'numeric';
      }
      if (this.item.is_multi_value) {
        return 'multi_value';
      }
      return 'single_value';
    },
    totalAverage() {
      if (this.data.statistics && this.data.statistics.platform_averages) {
        return this.data.statistics.platform_averages[this.researchItem.id] || null;
      }
      return null;
    },
    monthAverage() {
      if (this.data.statistics && this.data.statistics.month_averages) {
        return this.data.statistics.month_averages[this.researchItem.id] || null;
      }
      return null;
    },
    industryAverage() {
      if (this.data.statistics && this.data.statistics.industry_averages) {
        return this.data.statistics.industry_averages[this.researchItem.id] || null;
      }
      return null;
    },
    previousCommercialsAverage() {
      if (this.data.statistics && this.data.statistics.previous_researches_averages) {
        return this.data.statistics.previous_researches_averages[this.researchItem.id] || null;
      }
      return null;
    },
  },
  created() {
    const itemView = ITEM_DATA_VIEW_OPTIONS[this.item.render_type][0];
    if (itemView) {
      this.frequencies = this.calculateFrequencies();
      this.selectedItemView = itemView;
    } else {
      this.selectedItemView = 'noData';
    }
  },
};
</script>

<style scoped>
.item-view-body {
  height: 400px;
  overflow: auto;
}

a.active .badge-default {
  color: white;
}

.card-header {
  overflow: hidden; /* Prevent content from overflowing */
}

.card-header table {
  table-layout: fixed; /* Table layout fixed to control column widths */
  width: 100%; /* Ensure the table stays inside the parent */
}

.title-truncated {
  white-space: nowrap; /* Prevents line breaks */
  overflow: hidden; /* Hides overflowing text */
  text-overflow: ellipsis; /* Adds an ellipsis (...) at the end */
}
</style>
